<template>
  <v-container fluid class="container">
    <v-card>
      <v-card-title class="pb-2">Admin</v-card-title>
      <v-card-text>
        <v-tabs v-model="activeTab" grow>
          <v-tab>Gunicorn Error Log</v-tab>
          <v-tab>Planning Tmp Files</v-tab>
          <v-tab>Export Planning</v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab" class="mt-4">
          <v-tab-item>
            <v-row dense align="end" class="mb-2">
              <v-col cols="12" sm="4" md="2">
                <v-text-field
                  v-model.number="lineLimit"
                  type="number"
                  min="1"
                  max="2000"
                  label="Lines"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="8" md="4">
                <v-btn
                  color="primary"
                  :loading="loading"
                  @click="fetchGunicornErrorLog"
                >
                  Refresh Log
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="errorText" type="error" dense outlined class="mb-3">
              {{ errorText }}
            </v-alert>

            <div v-if="logPath" class="text-caption mb-2">
              Showing {{ lineCount }} lines from {{ logPath }}
            </div>

            <v-sheet class="log-viewer">
              <pre>{{ logText }}</pre>
            </v-sheet>
          </v-tab-item>
          <v-tab-item>
            <v-row dense align="end" class="mb-2">
              <v-col cols="12" sm="4" md="2">
                <v-text-field
                  v-model.number="tmpPlanningId"
                  type="number"
                  min="1"
                  label="Planning ID"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="8" md="4">
                <v-btn
                  color="primary"
                  :loading="tmpLoading"
                  @click="fetchPlanningTmpFiles"
                >
                  Load Files
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="tmpErrorText" type="error" dense outlined class="mb-3">
              {{ tmpErrorText }}
            </v-alert>

            <div v-if="tmpPath" class="text-caption mb-2">
              Showing {{ tmpFileCount }} files from {{ tmpPath }}
            </div>

            <v-simple-table v-if="tmpFiles.length" dense>
              <thead>
                <tr>
                  <th>File</th>
                  <th class="text-right">Size</th>
                  <th class="text-right">Modified</th>
                  <th class="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in tmpFiles" :key="file.name">
                  <td>{{ file.name }}</td>
                  <td class="text-right">{{ formatBytes(file.size_bytes) }}</td>
                  <td class="text-right">{{ formatTimestamp(file.modified_timestamp) }}</td>
                  <td class="text-right">
                    <v-btn
                      color="primary"
                      small
                      :loading="downloadingFileName === file.name"
                      @click="downloadTmpFile(file.name)"
                    >
                      Download
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-sheet v-else class="empty-state">
              No files loaded.
            </v-sheet>
          </v-tab-item>
          <v-tab-item>
            <div class="text-caption mb-3">
              Export a single planning as an importable <code>.sql</code> file.
              Keeps the original planning id and re-importing overwrites the same
              planning locally.
            </div>
            <v-row dense align="end" class="mb-2">
              <v-col cols="12" sm="4" md="2">
                <v-text-field
                  v-model.number="exportPlanningId"
                  type="number"
                  min="1"
                  label="Planning ID"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="8" md="4">
                <v-btn
                  color="primary"
                  :loading="exportLoading"
                  @click="downloadPlanningSqlExport"
                >
                  Export SQL
                </v-btn>
              </v-col>
            </v-row>

            <v-alert v-if="exportErrorText" type="error" dense outlined class="mb-3">
              {{ exportErrorText }}
            </v-alert>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '../api/lbt-api';

const {
  getGunicornErrorLog,
  getPlanningTmpFiles,
  downloadPlanningTmpFile,
  downloadPlanningSqlExport,
} = api;

export default {
  name: 'Admin',
  data() {
    return {
      activeTab: 0,
      lineLimit: 500,
      loading: false,
      errorText: '',
      logPath: '',
      lineCount: 0,
      logText: 'Loading...',
      tmpPlanningId: null,
      tmpLoading: false,
      tmpErrorText: '',
      tmpPath: '',
      tmpFileCount: 0,
      tmpFiles: [],
      downloadingFileName: '',
      exportPlanningId: null,
      exportLoading: false,
      exportErrorText: '',
    };
  },
  async created() {
    await this.fetchGunicornErrorLog();
  },
  methods: {
    async fetchGunicornErrorLog() {
      this.loading = true;
      this.errorText = '';
      try {
        const safeLineLimit = Number(this.lineLimit);
        const normalizedLineLimit = Number.isFinite(safeLineLimit)
          ? Math.trunc(safeLineLimit)
          : 500;
        const lines = Number.isFinite(normalizedLineLimit)
          ? Math.min(Math.max(normalizedLineLimit, 1), 2000)
          : 500;
        this.lineLimit = lines;
        const response = await getGunicornErrorLog(lines);
        this.logPath = response.path || '';
        this.lineCount = response.line_count || 0;
        this.logText = Array.isArray(response.lines) && response.lines.length
          ? response.lines.join('\n')
          : 'No log lines available.';
      } catch (e) {
        this.errorText = e.message || 'Failed to load gunicorn.error.log';
        this.logText = 'Could not load log.';
      } finally {
        this.loading = false;
      }
    },
    normalizePlanningId(value) {
      const parsedPlanningId = Number(value);
      if (!Number.isFinite(parsedPlanningId)) {
        return null;
      }
      const normalizedPlanningId = Math.trunc(parsedPlanningId);
      if (normalizedPlanningId < 1) {
        return null;
      }
      return normalizedPlanningId;
    },
    formatBytes(sizeBytes) {
      const size = Number(sizeBytes);
      if (!Number.isFinite(size) || size < 0) {
        return '-';
      }

      if (size < 1024) {
        return `${size} B`;
      }

      const units = ['KB', 'MB', 'GB', 'TB'];
      let value = size / 1024;
      let unitIndex = 0;
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
      }
      return `${value.toFixed(1)} ${units[unitIndex]}`;
    },
    formatTimestamp(unixTimestamp) {
      const parsedTimestamp = Number(unixTimestamp);
      if (!Number.isFinite(parsedTimestamp) || parsedTimestamp <= 0) {
        return '-';
      }
      return new Date(parsedTimestamp * 1000).toLocaleString();
    },
    async fetchPlanningTmpFiles() {
      this.tmpLoading = true;
      this.tmpErrorText = '';

      const planningId = this.normalizePlanningId(this.tmpPlanningId);
      if (!planningId) {
        this.tmpLoading = false;
        this.tmpErrorText = 'Please enter a valid planning ID.';
        this.tmpFiles = [];
        this.tmpPath = '';
        this.tmpFileCount = 0;
        return;
      }

      this.tmpPlanningId = planningId;
      try {
        const response = await getPlanningTmpFiles(planningId);
        this.tmpPath = response.path || '';
        this.tmpFileCount = response.file_count || 0;
        this.tmpFiles = Array.isArray(response.files) ? response.files : [];
      } catch (e) {
        this.tmpErrorText = e.message || 'Failed to load tmp files';
        this.tmpFiles = [];
        this.tmpPath = '';
        this.tmpFileCount = 0;
      } finally {
        this.tmpLoading = false;
      }
    },
    async downloadTmpFile(fileName) {
      const planningId = this.normalizePlanningId(this.tmpPlanningId);
      if (!planningId || !fileName) {
        return;
      }

      this.downloadingFileName = fileName;
      this.tmpErrorText = '';
      try {
        const response = await downloadPlanningTmpFile(planningId, fileName);
        const blobUrl = window.URL.createObjectURL(response.blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = response.fileName || fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        this.tmpErrorText = e.message || 'Failed to download file.';
      } finally {
        this.downloadingFileName = '';
      }
    },
    async downloadPlanningSqlExport() {
      const planningId = this.normalizePlanningId(this.exportPlanningId);
      if (!planningId || planningId < 1) {
        this.exportErrorText = 'Please enter a valid planning ID.';
        return;
      }

      this.exportLoading = true;
      this.exportErrorText = '';
      try {
        const response = await downloadPlanningSqlExport(planningId);
        const blobUrl = window.URL.createObjectURL(response.blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = response.fileName || `planning_${planningId}.sql`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        this.exportErrorText = e.message || 'Failed to export planning.';
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.log-viewer {
  max-height: 70vh;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #f8f8f8;
}

.log-viewer pre {
  margin: 0;
  padding: 12px;
  font-family: "Courier New", monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre;
}

.empty-state {
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #f8f8f8;
}
</style>
